import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { filterExpressComponents } from '@/utils/expressPayment';

type ExpressComponentLoadingState = { isLoading: boolean; isAvailable: boolean };
type ExpressComponentLoadingStates = Record<string, ExpressComponentLoadingState>;

export function useExpressComponents() {
  const { paymentMethodsAvailable } = useContext(CheckoutContext);

  const expressComponents = useMemo(() => filterExpressComponents(paymentMethodsAvailable), [paymentMethodsAvailable]);
  const [loadingStates, setLoadingStates] = useState<ExpressComponentLoadingStates>(() =>
    expressComponents.reduce<ExpressComponentLoadingStates>(
      (acc, config) => ({
        ...acc,
        [config.key]: { isLoading: true, isAvailable: false },
      }),
      {}
    )
  );
  const { isAllComponentsLoaded, isComponentsAvailable } = useMemo(
    () => ({
      isAllComponentsLoaded: Object.values(loadingStates).every((value) => !value.isLoading),
      isComponentsAvailable: Object.values(loadingStates).some((value) => value.isAvailable),
    }),
    [loadingStates]
  );

  useEffect(() => {
    setLoadingStates((prev) =>
      expressComponents.reduce<ExpressComponentLoadingStates>(
        (acc, config) => ({ ...acc, [config.key]: prev[config.key] ?? { isLoading: true, isAvailable: false } }),
        {}
      )
    );
  }, [expressComponents]);

  const handleComponentReady = useCallback((key: string, isAvailable: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [key]: { isLoading: false, isAvailable } }));
  }, []);

  return {
    isAllComponentsLoaded,
    isComponentsAvailable,
    expressComponents,
    handleComponentReady,
  };
}
